import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  successMsg: { color: "#4caf50", fontSize: "13px" }
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(String);

const STATUS_TONES = {
  draft: "muted",
  approved: "success",
  paid: "info"
};

class PayrollList extends Component {
  state = {
    payrolls: [],
    staff: [],
    selectedMonth: MONTHS[new Date().getMonth()],
    selectedYear: String(CURRENT_YEAR),
    loading: false,
    open: false,
    form: { staffId: "", basicSalary: "", allowances: "", deductions: "", nssf: "", paye: "", netSalary: "" },
    saving: false,
    error: null,
    success: null
  };

  componentDidMount() {
    this.fetchStaff();
    this.fetchPayroll();
  }

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff?limit=100");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchPayroll = async () => {
    const { selectedMonth, selectedYear } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/hr/payroll", { params: { month: selectedMonth, year: selectedYear, limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ payrolls: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  calcNet = (form) => {
    const basic = parseFloat(form.basicSalary) || 0;
    const allowances = parseFloat(form.allowances) || 0;
    const deductions = parseFloat(form.deductions) || 0;
    const nssf = parseFloat(form.nssf) || 0;
    const paye = parseFloat(form.paye) || 0;
    return Math.max(0, basic + allowances - deductions - nssf - paye);
  };

  handleFormChange = field => e => {
    this.setState(prev => {
      const form = { ...prev.form, [field]: e.target.value };
      form.netSalary = String(this.calcNet(form));
      return { form };
    });
  };

  handleSave = async () => {
    const { form, selectedMonth, selectedYear } = this.state;
    if (!form.staffId || !form.basicSalary) {
      this.setState({ error: "Staff and basic salary are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/hr/payroll", {
        ...form,
        month: selectedMonth, year: selectedYear,
        basicSalary: parseFloat(form.basicSalary) || 0,
        allowances: parseFloat(form.allowances) || 0,
        deductions: parseFloat(form.deductions) || 0,
        nssf: parseFloat(form.nssf) || 0,
        paye: parseFloat(form.paye) || 0,
        netSalary: this.calcNet(form)
      });
      this.setState({ open: false, saving: false, success: "Payroll entry saved." });
      this.fetchPayroll();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed.", saving: false });
    }
  };

  handleApprove = async (id) => {
    try {
      await api.patch(`/hr/payroll/${id}/approve`);
      this.fetchPayroll();
    } catch (e) { /* silent */ }
  };

  handleMarkPaid = async (id) => {
    try {
      await api.patch(`/hr/payroll/${id}/pay`);
      this.fetchPayroll();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { payrolls, staff, selectedMonth, selectedYear, loading, open, form, saving, error, success } = this.state;

    const totalNet = payrolls.reduce((sum, p) => sum + (p.netSalary || 0), 0);

    const columns = [
      {
        id: "staff",
        header: "Staff",
        accessorFn: p => {
          const s = p.staff || {};
          return `${s.firstName || ""} ${s.lastName || ""}`.trim() || "—";
        }
      },
      { id: "role", header: "Role", accessorFn: p => p.staff?.role || "—" },
      {
        id: "basic",
        header: "Basic (UGX)",
        accessorFn: p => p.basicSalary || 0,
        meta: { align: "right" },
        cell: info => formatUGX(info.getValue())
      },
      {
        id: "allowances",
        header: "Allowances (UGX)",
        accessorFn: p => p.allowances || 0,
        meta: { align: "right" },
        cell: info => formatUGX(info.getValue())
      },
      {
        id: "deductions",
        header: "Deductions (UGX)",
        accessorFn: p => (p.nssf || 0) + (p.paye || 0) + (p.deductions || 0),
        meta: { align: "right" },
        cell: info => formatUGX(info.getValue())
      },
      {
        id: "netPay",
        header: "Net Pay (UGX)",
        accessorFn: p => p.netSalary || 0,
        meta: { align: "right" },
        cell: info => <strong style={{ color: "#1a237e" }}>{formatUGX(info.getValue())}</strong>
      },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "draft";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const p = info.row.original;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              {p.status === "draft" && (
                <Button size="sm" color="success" simple onClick={() => this.handleApprove(p.id)}>Approve</Button>
              )}
              {p.status === "approved" && (
                <Button size="sm" color="info" simple onClick={() => this.handleMarkPaid(p.id)}>Mark Paid</Button>
              )}
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Payroll</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Monthly staff salary processing
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={() => this.setState({
                    open: true, error: null, success: null,
                    form: { staffId: "", basicSalary: "", allowances: "", deductions: "", nssf: "", paye: "", netSalary: "" }
                  })}>
                    + Add Entry
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {success && <p className={classes.successMsg}>&#10003; {success}</p>}

              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
                <FormControl style={{ minWidth: "160px" }}>
                  <InputLabel>Month</InputLabel>
                  <Select value={selectedMonth} onChange={e => this.setState({ selectedMonth: e.target.value }, this.fetchPayroll)}>
                    {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl style={{ minWidth: "100px" }}>
                  <InputLabel>Year</InputLabel>
                  <Select value={selectedYear} onChange={e => this.setState({ selectedYear: e.target.value }, this.fetchPayroll)}>
                    {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </div>

              <DataTable
                columns={columns}
                data={payrolls}
                isLoading={loading}
                emptyState={`No payroll entries for ${selectedMonth} ${selectedYear}.`}
              />
            </CardBody>
            {payrolls.length > 0 && (
              <CardFooter style={{ justifyContent: "flex-end", padding: "8px 20px 12px" }}>
                <span style={{ fontSize: "14px", color: "#333" }}>
                  Total Net Pay: <strong style={{ color: "#1a237e", fontSize: "16px" }}>UGX {totalNet.toLocaleString()}</strong>
                </span>
              </CardFooter>
            )}
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={`Add Payroll Entry — ${selectedMonth} ${selectedYear}`} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Staff Member *</InputLabel>
              <Select value={form.staffId} onChange={this.handleFormChange("staffId")}>
                <MenuItem value="">— Select Staff —</MenuItem>
                {staff.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} — {s.role || ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <GridContainer>
              <GridItem xs={6}>
                <CustomInput labelText="Basic Salary (UGX) *" id="basicSalary" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.basicSalary, onChange: this.handleFormChange("basicSalary"), type: "number", min: "0" }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Allowances (UGX)" id="allowances" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.allowances, onChange: this.handleFormChange("allowances"), type: "number", min: "0" }} />
              </GridItem>
              <GridItem xs={4}>
                <CustomInput labelText="NSSF (UGX)" id="nssf" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.nssf, onChange: this.handleFormChange("nssf"), type: "number", min: "0" }} />
              </GridItem>
              <GridItem xs={4}>
                <CustomInput labelText="PAYE (UGX)" id="paye" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.paye, onChange: this.handleFormChange("paye"), type: "number", min: "0" }} />
              </GridItem>
              <GridItem xs={4}>
                <CustomInput labelText="Other Deductions" id="deductions" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.deductions, onChange: this.handleFormChange("deductions"), type: "number", min: "0" }} />
              </GridItem>
            </GridContainer>
            <div style={{ marginTop: "12px", padding: "12px", background: "#e8eaf6", borderRadius: "6px", textAlign: "center" }}>
              <span style={{ fontWeight: "bold", color: "#1a237e", fontSize: "16px" }}>
                Net Pay: UGX {Number(this.calcNet(form)).toLocaleString()}
              </span>
            </div>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

PayrollList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(PayrollList));
